import {
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import c from "lib/style/colorVar.scss";
import { useNavigate } from "react-router-dom";
import { emailFormatValidator } from "utils/formValidation";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import { forgetPassword } from "./model";

const PAGE_LOCALE_KEY = "page.forgetPassword.";

const ForgetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { snackbarError } = useSnackbarMAS();

  //Uses null to distinguish untouched from empty
  const [email, setEmail] = useState(null);

  const [isAwaitingSendEmailResponse, setIsAwaitingSendEmailResponse] =
    useState(false);

  const invalidChecking = useMemo(() => {
    return {
      emailEmpty: !email,
      emailEmptyTouched: email !== null && !email,
      emailInvalid: !emailFormatValidator(email),
    };
  }, [email]);
  const areFieldsInvalid = Object.values(invalidChecking).some(Boolean);

  const usernameHelperText = () => {
    if (invalidChecking.emailEmptyTouched) {
      return t(PAGE_LOCALE_KEY + "fields.email.error.empty");
    } else if (invalidChecking.emailInvalid && !invalidChecking.emailEmpty) {
      return t(PAGE_LOCALE_KEY + "fields.email.error.format");
    }
    return " "; //workaround to keep helper text expanded, see https://mui.com/material-ui/react-text-field/#helper-text
  };

  const handleSendForgetPasswordEmail = async () => {
    invalidChecking.emailEmpty && setEmail("");
    if (areFieldsInvalid) return;

    const payload = {
      email: email,
    };

    setIsAwaitingSendEmailResponse(true);
    const apiResponse = await forgetPassword(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbarError({ message: t("general.message.networkError") });
      setIsAwaitingSendEmailResponse(false);
      return;
    }

    switch (apiResponse.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        snackbarError({ message: t("general.message.unauthorized") });
        break;
      }
      default: {
      }
    }

    const responseBody = apiResponse?.data;
    switch (responseBody.responseCode) {
      case "0000":
      case "4101": //use successful flow even if user isn't found for security purposes
        navigate("/user/forget-password-result", { replace: true });
        break;
      case "5999":
        snackbarError({
          message: t(
            PAGE_LOCALE_KEY + `message.fail.${responseBody.responseCode}`,
          ),
        });
        break;
      default: {
        snackbarError({
          message: t(PAGE_LOCALE_KEY + "message.fail"),
        });
      }
    }

    setIsAwaitingSendEmailResponse(false);
  };

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      gap="8px"
      sx={{ width: "380px", maxWidth: "80%" }}
    >
      <Typography textAlign="center" variant="h2" sx={{ marginY: "24px" }}>
        {t(PAGE_LOCALE_KEY + "label.title")}
      </Typography>
      <TextField
        disabled={isAwaitingSendEmailResponse}
        variant="standard"
        label={t(PAGE_LOCALE_KEY + "fields.email.label")}
        InputLabelProps={{ shrink: true }}
        value={email ?? ""}
        onChange={(e) => setEmail(e.target.value)}
        helperText={usernameHelperText()}
        FormHelperTextProps={{ sx: { color: "red" } }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            loginMasterPortal();
          }
        }}
        sx={{
          "& .MuiInput-root": {
            height: "40px",
          },
        }}
      />
      <Button
        disabled={areFieldsInvalid || isAwaitingSendEmailResponse}
        variant="auth-button"
        onClick={handleSendForgetPasswordEmail}
        endIcon={
          isAwaitingSendEmailResponse && (
            <CircularProgress size={16} sx={{ color: c["blackDark999"] }} />
          )
        }
      >
        {t(PAGE_LOCALE_KEY + "button.actionSubmit")}
      </Button>
      <Link
        variant="button-normal"
        color={c["brand1"]}
        underline="none"
        href=""
        onClick={() => {
          navigate("/user/login");
        }}
        textAlign="center"
      >
        {t(PAGE_LOCALE_KEY + "link.back")}
      </Link>
    </Stack>
  );
};

export default ForgetPassword;
